// @ts-nocheck
import * as React from "react";
import { Logo, Ic, Sidebar, Topbar, LineChart, BarChart, Kpi, AppFooter, useCurrentUser, PhoneInput, apiErrorMessage, OrgCombobox } from "./common";
import { api, careManager } from "../api";
import { usePermissionsFromSession } from "../components/permissions";
import { Modal, Field } from "../components/Modal";
import { DeletionConfirmModal } from "../components/DeletionConfirmModal";
import { ManagerAssignCaseModal } from "../components/ManagerAssignCaseModal";
import { useSaveToast } from "../components/SaveToast";
import { LocationPicker } from "../components/naver/LocationPicker";

const EMPTY_MGR = {
  name: "", age: 30, qualification: "사회복지사 1급", organization_id: null, region_id: null,
  phone: "", license_no: "", assigned_cases: 0, load_ratio: 0,
  weekly_hours: 0, max_weekly_hours: 40, rating: 4.5, status: "available",
  latitude: null, longitude: null, license_verified: false, license_verification_note: "",
};

// Operator-editable field sets per modal mode. Stats (assigned_cases,
// load_ratio, weekly_hours, rating) are excluded — they come from real
// activity (cases, visits, feedback) and shouldn't be hand-edited.
// max_weekly_hours는 계약 조건(시간제·전일제)이라 운영자 설정 대상.
// region_id는 직접 입력이 아니라 organization 선택에서 파생되지만(selectOrg),
// 저장 페이로드에는 반드시 실어야 생성 시 null로 남거나 기관 변경 후 권역이
// 옛 값으로 어긋나는 일을 막을 수 있다. 컨트롤러 manager_params도 이미 허용.
// license_no는 매니저 본인 가입 시 등록되므로 운영자 편집 페이로드에서 제외한다
// (운영자는 검증만 — license_verified/license_verification_note는 유지).
// 신규 등록 경로는 폐지(회원가입→승인으로만 생성)되어 편집 필드만 남는다.
const EDIT_FIELDS = ["name", "age", "qualification", "organization_id", "region_id", "phone", "license_verified", "license_verification_note", "status", "max_weekly_hours", "latitude", "longitude"];

function pick(obj, keys) { return keys.reduce((a, k) => { a[k] = obj[k]; return a; }, {}); }

// api.ts throws "<status> <statusText> <json-body>" on non-2xx responses.
// Prefer the backend's own `message` (or `error`) field — keeps the message
// accurate as the permission matrix moves roles around. Falls back to a
// generic copy if the body wasn't JSON.
function humanizeError(e) {
  return apiErrorMessage(e);
}

const STATUS_LABEL = { available: "가용", busy: "바쁨", overloaded: "과부하", moving: "이동중", offline: "오프라인" };
const STATUS_COLOR = { available: "green", busy: "amber", overloaded: "rose", moving: "amber", offline: "gray" };

const SORT_OPTIONS = [
  { key: "id",          label: "등록 순",       cmp: (a,b)=> a.id - b.id },
  { key: "name",        label: "이름 순",       cmp: (a,b)=> a.name.localeCompare(b.name, 'ko') },
  { key: "cases_desc",  label: "케이스 많은 순", cmp: (a,b)=> b.assigned_cases - a.assigned_cases },
  { key: "load_asc",    label: "여유 많은 순",   cmp: (a,b)=> a.load_ratio - b.load_ratio },
  { key: "rating_desc", label: "평가 높은 순",   cmp: (a,b)=> b.rating - a.rating },
];

function Management() {
  const { user, loaded: userLoaded } = useCurrentUser();
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });
  const [managers, setManagers] = React.useState([]);
  const [orgs, setOrgs] = React.useState([]);
  const [regions, setRegions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(() => {
    // Honour ?id=N so dashboard "배정 →" deep-links to a specific manager row.
    const raw = new URLSearchParams(window.location.search).get("id");
    return raw ? Number(raw) : null;
  });
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_MGR);
  const [err, setErr] = React.useState(null);
  const { notifySaved, saveToast } = useSaveToast();
  const [q, setQ] = React.useState("");
  // 검색어는 키스트로크마다 즉시 조회하지 않고 디바운스(350ms)된 dq로만 API를 친다.
  // 드롭다운 필터(자격/상태/소속/권역)는 변경 빈도가 낮아 즉시 조회한다.
  const [dq, setDq] = React.useState("");
  const [filterQual, setFilterQual] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [orgFilter, setOrgFilter] = React.useState("all");
  const [regionFilter, setRegionFilter] = React.useState("");
  const [sortKey, setSortKey] = React.useState("id");
  const [deletePrompt, setDeletePrompt] = React.useState(null);
  const [assignTarget, setAssignTarget] = React.useState(null);
  // 소속 변경 모달(운영자 push). 전체 편집(canManageManagers, system_admin)과 분리된
  // 권역 관리자용 좁은 흐름 — 대상 매니저를 다른 care 기관으로 재배정. 사유 필수.
  const [transfer, setTransfer] = React.useState(null); // { manager, orgId, reason, err, saving }
  const [retire, setRetire] = React.useState(null);     // { manager, reason, err, saving }
  // 복직 시 소속 기관 선택 모달 — 퇴직 중 소속 기관이 삭제돼 미소속이 된 매니저만 대상.
  const [reinstateModal, setReinstateModal] = React.useState(null); // { manager, orgId, err, saving }

  // q 입력이 멈춘 뒤(350ms) dq를 갱신 → 키스트로크마다 API를 치지 않는다.
  React.useEffect(() => {
    const t = setTimeout(() => setDq(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const query = {};
      if (dq)                         query.q = dq;
      if (filterQual)                 query.qualification = filterQual;
      // "퇴직"은 운영 status가 아니라 별도 축(retired_at)이라 retired 파라미터로 보낸다.
      // "all"=재직자 전체(기본·퇴직자 숨김), "all_incl_retired"=재직+퇴직 모두,
      // "retired"=퇴직자만. 그 외 값은 재직자 한정 status 필터.
      if (statusFilter === "retired")               query.retired = "only";
      else if (statusFilter === "all_incl_retired") query.retired = "all";
      else if (statusFilter !== "all")              query.status = statusFilter;
      if (orgFilter !== "all")        query.organization_id = orgFilter;
      if (regionFilter)               query.region_id = regionFilter;
      // 매니저 목록만 필터에 따라 재조회한다. 소속·권역 드롭다운 데이터는
      // 필터와 무관하게 동일하므로 마운트 시 1회만 받는다(아래 별도 effect).
      const m = await api.list("care_managers", query);
      setManagers(m);
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setLoading(false); }
  }, [dq, filterQual, statusFilter, orgFilter, regionFilter]);
  React.useEffect(() => { load(); }, [load]);

  // 소속/권역 드롭다운 채우기용 — 필터와 무관해 마운트 시 1회만 조회한다.
  // organizations는 전체 직렬화(행당 trends·contact N+1 + 10MB)를 피해
  // {id,name,region_id,category} 최소 필드만 받는다(minimal=1).
  React.useEffect(() => {
    let live = true;
    Promise.all([
      // 케어매니저는 care 카테고리 기관에만 속하므로 드롭다운(필터·편집·이관)도
      // care 기관만 필요하다. category=care로 좁혀 system_admin이 전국 33k 전체
      // 기관(병원·복지시설 포함)을 받던 2.8MB 페이로드를 수천 건으로 줄인다.
      api.list("organizations", { minimal: "1", category: "care" }),
      api.list("regions"),
    ]).then(([o, r]) => {
      if (!live) return;
      setOrgs(o);
      setRegions(r);
    }).catch(e => { if (live) setErr(apiErrorMessage(e)); });
    return () => { live = false; };
  }, []);

  const visible = React.useMemo(() => {
    const cmp = SORT_OPTIONS.find(s => s.key === sortKey)?.cmp;
    return cmp ? [...managers].sort(cmp) : managers;
  }, [managers, sortKey]);


  const selected = visible.find(m => m.id === selectedId) || visible[0];

  // 권역 대시보드의 "케이스 배정" 딥링크 처리.
  // /managers?id=N&action=assign 로 진입 시 매니저가 로드된 직후 배정 모달을
  // 한 번만 자동 오픈한다. 모달 닫은 뒤에는 쿼리에서 action을 제거해 새로고침
  // 시 모달이 다시 뜨지 않게 한다.
  const autoAssignAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoAssignAppliedRef.current) return;
    if (loading || !selected) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("action") !== "assign") return;
    if (Number(url.searchParams.get("id")) !== selected.id) return;
    autoAssignAppliedRef.current = true;
    setAssignTarget(selected);
    url.searchParams.delete("action");
    window.history.replaceState({}, "", url.toString());
  }, [loading, selected]);

  const openEdit = (m) => { setForm({ ...m }); setEditing(m.id); setErr(null); };
  const close    = () => { setEditing(null); setErr(null); };

  const save = async () => {
    // 소속 기관은 필수(요양/재가 기관). 서버도 막지만 제출 전에 친절히 안내한다.
    if (!form.organization_id) {
      setErr("소속 기관을 선택해주세요. (케어매니저는 요양/재가 기관 소속이 필요합니다)");
      return;
    }
    // 자격증 사진 첨부는 매니저 본인 가입 경로에서만 한다. 운영자 편집 다이얼로그는
    // 사진을 업로드하지 않고 검증(license_verified)만 하므로 JSON으로 저장한다.
    // 케어매니저 신규 등록은 운영자가 하지 않는다(회원가입→승인 경로로만 생성).
    try {
      const payload = pick(form, EDIT_FIELDS);
      await api.update("care_managers", editing, payload);
      await load();
      close();
      notifySaved();
    } catch (e) { setErr(humanizeError(e)); }
  };

  const askDelete = async (m) => {
    setDeletePrompt({ manager: m, impact: null, loading: true });
    try {
      const res = await fetch(`/api/v1/care_managers/${m.id}/deletion_impact`, { credentials: "same-origin" });
      const j = await res.json().catch(() => ({}));
      setDeletePrompt({ manager: m, impact: j.impact || {}, deletable: j.deletable !== false, loading: false });
    } catch { setDeletePrompt({ manager: m, impact: {}, deletable: true, loading: false }); }
  };
  const cancelDelete  = () => setDeletePrompt(null);
  const confirmDelete = async () => {
    if (!deletePrompt) return;
    try {
      await api.remove("care_managers", deletePrompt.manager.id);
      setDeletePrompt(null);
      await load();
    } catch (e) { alert(humanizeError(e)); }
  };

  const openTransfer = (m) =>
    setTransfer({ manager: m, orgId: m.organization_id ?? null, reason: "", err: null, saving: false });
  const submitTransfer = async () => {
    if (!transfer) return;
    if (!transfer.orgId) { setTransfer(t => ({ ...t, err: "이동할 소속 기관을 선택해주세요." })); return; }
    if (!transfer.reason.trim()) { setTransfer(t => ({ ...t, err: "소속 변경 사유를 입력해주세요." })); return; }
    setTransfer(t => ({ ...t, saving: true, err: null }));
    try {
      await careManager.transferOrg(transfer.manager.id, transfer.orgId, transfer.reason.trim());
      setTransfer(null);
      await load();
      notifySaved("소속 기관이 변경되었습니다");
    } catch (e) { setTransfer(t => ({ ...t, saving: false, err: humanizeError(e) })); }
  };

  // 퇴직 처리(사유 필수) — 소속·이력은 보존하고 명부·매칭에서만 제외한다.
  const openRetire = (m) => setRetire({ manager: m, reason: "", err: null, saving: false });
  const submitRetire = async () => {
    if (!retire) return;
    if (!retire.reason.trim()) { setRetire(t => ({ ...t, err: "퇴직 처리 사유를 입력해주세요." })); return; }
    setRetire(t => ({ ...t, saving: true, err: null }));
    try {
      await careManager.retire(retire.manager.id, retire.reason.trim());
      setRetire(null);
      await load();
      notifySaved("퇴직 처리되었습니다");
    } catch (e) { setRetire(t => ({ ...t, saving: false, err: humanizeError(e) })); }
  };
  // 복직 — 소속이 살아 있으면 사유 없이 즉시(확인만). 퇴직 중 소속 기관이 삭제돼
  // 미소속이 된 매니저는 재직 전환 시 소속이 필수이므로 기관 선택 모달을 띄운다.
  const reinstate = async (m) => {
    if (!m.organization_id) {
      setReinstateModal({ manager: m, orgId: null, err: null, saving: false });
      return;
    }
    if (!window.confirm(`${m.name} 매니저를 복직 처리합니다. 다시 명부·매칭에 포함됩니다.`)) return;
    try {
      await careManager.unretire(m.id);
      await load();
      notifySaved("복직 처리되었습니다");
    } catch (e) { alert(humanizeError(e)); }
  };
  const submitReinstate = async () => {
    if (!reinstateModal) return;
    if (!reinstateModal.orgId) { setReinstateModal(t => ({ ...t, err: "복직할 소속 기관을 선택해주세요." })); return; }
    setReinstateModal(t => ({ ...t, saving: true, err: null }));
    try {
      await careManager.unretire(reinstateModal.manager.id, undefined, reinstateModal.orgId);
      setReinstateModal(null);
      await load();
      notifySaved("복직 처리되었습니다");
    } catch (e) { setReinstateModal(t => ({ ...t, saving: false, err: humanizeError(e) })); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // 매니저 권역은 소속기관의 권역에서 파생한다(독립 입력값이 아님). 기관을 고르면
  // region_id를 그 기관의 region_id로 동기화해 목록/권역격리에서 권역이 어긋나지
  // 않게 한다. 기관 미배정(null)이면 권역도 null. orgs는 as_json에 region_id 포함.
  const selectOrg = (orgId) => setForm((f) => {
    const org = orgs.find((o) => o.id === orgId);
    return { ...f, organization_id: orgId, region_id: org ? org.region_id : null };
  });

  const availableCount = managers.filter(m => m.status === "available").length;
  const quals = ["사회복지사 1급","사회복지사 2급","요양보호사 1급","요양보호사 2급","물리치료사","간호조무사"];

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="manager"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="케어매니저 관리" sub={`등록 ${managers.length.toLocaleString()}명 · 가용 ${availableCount.toLocaleString()}명`}/>
          <div className="main">

            <div className="card" style={{padding:14, marginBottom:14, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
              <div className="searchbar" style={{flex:'0 0 240px', position:'relative'}}>
                <span style={{position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--ink-3)'}}>{Ic.search}</span>
                <input className="input" style={{paddingLeft:36}} placeholder="이름·자격·전화 검색" value={q} onChange={e=>setQ(e.target.value)}/>
              </div>
              <select className="select" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{width:110}}>
                <option value="all">재직 전체</option>
                <option value="all_incl_retired">전체(퇴직 포함)</option>
                {Object.entries(STATUS_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                <option value="retired">퇴직</option>
              </select>
              <select className="select" value={filterQual} onChange={e=>setFilterQual(e.target.value)} style={{width:140}}>
                <option value="">전체 자격</option>
                {quals.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
              {/* 권역 드롭다운은 전 권역을 한 목록에서 보는 system_admin 전용.
                  region_admin은 이미 자기 권역으로 스코프되어 의미 없으므로 숨긴다. */}
              {perms.role === "system_admin" && regions.length > 0 && (
                <select className="select" value={regionFilter} onChange={e=>setRegionFilter(e.target.value)} style={{width:140}}>
                  <option value="">전체 권역</option>
                  {regions.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                </select>
              )}
              <OrgCombobox
                orgs={orgs}
                value={orgFilter === "all" ? null : Number(orgFilter)}
                onChange={id => setOrgFilter(id == null ? "all" : String(id))}
                allLabel="전체 소속기관"
                width={180}
              />
              <select className="select" value={sortKey} onChange={e=>setSortKey(e.target.value)} style={{width:140}}>
                {SORT_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <span className="muted" style={{fontSize:11.5, marginLeft:6}}>{visible.length}명</span>
              <div style={{flex:1}}/>
              {perms.canExportCareManagers && (
                <a className="btn btn-ghost btn-md" href="/api/v1/care_managers/export.csv" style={{textDecoration:'none'}}>{Ic.arrow} CSV</a>
              )}
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr 380px', gap:14}}>
              <div className="card" style={{overflow:'hidden'}}>
                <table className="tbl">
                  <thead><tr>
                    <th style={{paddingLeft:22}}>매니저</th>
                    <th>자격</th><th>소속 권역</th><th>소속 기관</th><th>담당</th><th style={{width:'16%'}}>부하</th><th>주간</th><th>평가</th><th>상태</th><th/>
                  </tr></thead>
                  <tbody>
                  {loading && <tr><td colSpan={9} className="muted" style={{padding:30, textAlign:'center'}}>불러오는 중…</td></tr>}
                  {visible.map((m,i)=>(
                    <tr key={m.id} style={{background: selected?.id===m.id?'var(--orange-50)':'transparent', cursor:'pointer'}} onClick={()=>setSelectedId(m.id)}>
                      <td style={{paddingLeft:22}}>
                        <div style={{display:'flex', alignItems:'center', gap:10}}>
                          <div className={"avatar sm " + (i%2?'green':'')}>{m.name[0]}</div>
                          <div>
                            <div style={{fontWeight:700, fontSize:12.5}}>{m.name}</div>
                            <div className="muted" style={{fontSize:10}}>{m.age}세</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="muted">{m.qualification}</span>
                        {m.has_license_image && (m.license_verified
                          ? <span className="badge badge-green" style={{marginLeft:6, fontSize:9}}>검증</span>
                          : <span className="badge badge-amber" style={{marginLeft:6, fontSize:9}}>미검증</span>)}
                      </td>
                      <td>{m.region_name||'-'}</td>
                      <td>{m.organization_name||'-'}</td>
                      <td className="mono"><b>{m.assigned_cases}</b></td>
                      <td>
                        <div style={{display:'flex', alignItems:'center', gap:8}}>
                          <div className="bar" style={{width:80}}><span style={{width:`${m.load_ratio*100}%`, background: m.load_ratio>=0.9?'var(--rose-500)':m.load_ratio>=0.7?'var(--amber-500)':'var(--green-500)'}}/></div>
                          <span className="mono" style={{fontSize:11}}>{Math.round(m.load_ratio*100)}%</span>
                        </div>
                      </td>
                      <td className="mono">{m.weekly_hours}/{m.max_weekly_hours ?? 40}h</td>
                      <td className="mono">{m.rating.toFixed(1)}</td>
                      <td>{m.retired
                        ? <span className="badge badge-gray">퇴직</span>
                        : <span className={"badge badge-" + STATUS_COLOR[m.status]}>{STATUS_LABEL[m.status]}</span>}</td>
                      <td style={{whiteSpace:'nowrap'}}>
                        {/* 퇴직자: 복직만 노출(편집·삭제·소속변경은 명부 복귀 후에). */}
                        {perms.canManageManagers && m.retired && (
                          <button className="btn btn-ghost btn-sm" onClick={(e)=>{e.stopPropagation(); reinstate(m);}} style={{color:'var(--green-600)'}}>복직</button>
                        )}
                        {perms.canManageManagers && !m.retired && (
                          <button className="btn btn-ghost btn-sm" onClick={(e)=>{e.stopPropagation(); openEdit(m);}}>편집</button>
                        )}
                        {perms.canManageManagers && !m.retired && (
                          <button className="btn btn-ghost btn-sm" onClick={(e)=>{e.stopPropagation(); openRetire(m);}}>퇴직</button>
                        )}
                        {perms.canManageManagers && !m.retired && (
                          <button className="btn btn-ghost btn-sm" onClick={(e)=>{e.stopPropagation(); askDelete(m);}} style={{color:'var(--rose-500)'}}>삭제</button>
                        )}
                        {/* 전체 편집 권한이 없는 권역 관리자에게는 소속 변경만 단독 노출
                            (system_admin은 위 '편집' 모달에서 기관까지 바꾸므로 중복 노출 안 함). */}
                        {perms.canTransferManagers && !perms.canManageManagers && !m.retired && (
                          <button className="btn btn-ghost btn-sm" onClick={(e)=>{e.stopPropagation(); openTransfer(m);}}>소속 변경</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>

              <div className="card" style={{overflow:'hidden'}}>
                {selected ? (
                  <>
                    <div style={{height:120, background:'linear-gradient(135deg, var(--orange-50), var(--green-50))', display:'grid', placeItems:'center'}}>
                      <div className="avatar lg" style={{width:64, height:64, fontSize:24}}>{selected.name[0]}</div>
                    </div>
                    <div style={{padding:'18px 22px 8px'}}>
                      <div className="h2" style={{fontSize:18}}>{selected.name} <span className="muted" style={{fontSize:13, fontWeight:500}}>· {selected.age}세</span></div>
                      <div className="muted" style={{fontSize:12, marginTop:4}}>{selected.qualification} · {selected.region_name||'권역 미배정'} · {selected.organization_name||'기관 미배정'}</div>
                    </div>
                    <div style={{padding:'12px 22px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
                      {[
                        {l:'담당 케이스', v:selected.assigned_cases},
                        {l:'주간 시간',  v:`${selected.weekly_hours} / ${selected.max_weekly_hours ?? 40}시간`},
                        {l:'부하율',     v:`${Math.round(selected.load_ratio*100)}%`},
                        {l:'평가 점수',   v:selected.rating.toFixed(1)},
                        {l:'상태',       v:STATUS_LABEL[selected.status]},
                        {l:'전화',       v:selected.phone||'-'},
                      ].map((f,i)=>(
                        <div key={i}>
                          <div className="muted" style={{fontSize:10, fontWeight:600}}>{f.l}</div>
                          <div style={{fontSize:12.5, fontWeight:600, marginTop:2}}>{f.v}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:6, flexWrap:'wrap'}}>
                      {perms.canManageCases && (
                        // 메인 배정 경로는 점수·근거 기반의 AI 자동매칭(COD)으로 유도.
                        // 직접 배정은 부하 분산·오버라이드용 보조 수단으로만 남긴다.
                        <a className="btn btn-primary btn-md" href="/cod"
                           style={{flex:1, minWidth:160, textDecoration:'none', textAlign:'center'}}
                           title="AI 자동매칭 화면에서 점수 기반으로 배정">AI 자동매칭으로 배정 →</a>
                      )}
                      {perms.canManageCases && (
                        <button className="btn btn-ghost btn-md" onClick={()=>setAssignTarget(selected)}
                          title="이 매니저에게 매칭 단계 케이스를 직접 배정(점수 우회)">직접 배정</button>
                      )}
                      {perms.canManageManagers && selected.retired && (
                        <button className="btn btn-ghost btn-md" onClick={()=>reinstate(selected)} style={{color:'var(--green-600)'}}
                          title="이 매니저를 복직 처리(명부·매칭에 다시 포함)">복직</button>
                      )}
                      {perms.canManageManagers && !selected.retired && (
                        <button className="btn btn-ghost btn-md" onClick={()=>openEdit(selected)}>편집</button>
                      )}
                      {perms.canManageManagers && !selected.retired && (
                        <button className="btn btn-ghost btn-md" onClick={()=>openRetire(selected)}
                          title="퇴사한 매니저를 명부·매칭에서 제외(소속·이력 보존)">퇴직</button>
                      )}
                      {perms.canManageManagers && !selected.retired && (
                        <button className="btn btn-ghost btn-md" onClick={()=>askDelete(selected)} style={{color:'var(--rose-500)'}}>삭제</button>
                      )}
                      {perms.canTransferManagers && !perms.canManageManagers && !selected.retired && (
                        <button className="btn btn-ghost btn-md" onClick={()=>openTransfer(selected)}
                          title="이 매니저를 다른 기관으로 재배정(소속 변경)">소속 변경</button>
                      )}
                      {!perms.canManageCases && !perms.canManageManagers && !perms.canTransferManagers && (
                        <div className="muted" style={{flex:1, textAlign:'center', fontSize:11, padding:'6px 0'}}>
                          편집 권한이 없습니다.
                        </div>
                      )}
                    </div>
                  </>
                ) : <div className="muted" style={{padding:30, textAlign:'center'}}>매니저를 선택하세요</div>}
              </div>
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      <Modal
        open={editing != null}
        title={`${form.name || ""} 편집`}
        onClose={close}
        width={560}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={close}>취소</button>
          <button className="btn btn-primary btn-md" onClick={save}>저장</button>
        </>}
      >
        {err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{err}</div>}
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
          <Field label="이름"><input className="input" value={form.name} onChange={e=>set('name')(e.target.value)}/></Field>
          <Field label="나이"><input className="input" type="number" value={form.age} onChange={e=>set('age')(+e.target.value)}/></Field>
          <Field label="자격증">
            <select className="select" value={form.qualification} onChange={e=>set('qualification')(e.target.value)}>
              <option>사회복지사 1급</option>
              <option>사회복지사 2급</option>
              <option>요양보호사 1급</option>
              <option>요양보호사 2급</option>
              <option>물리치료사</option>
              <option>간호조무사</option>
            </select>
          </Field>
          <Field label="소속 기관 (필수)">
            {/* 케어매니저는 요양/재가(care) 기관에만 소속 가능 → care 분류만 노출. */}
            <OrgCombobox
              orgs={orgs}
              value={form.organization_id ?? null}
              onChange={id => selectOrg(id)}
              placeholder="기관 이름 검색 (필수)"
              width="100%"
            />
          </Field>
          <Field label="소속 권역 (소속 기관에 따라 자동)">
            {/* 권역은 기관에서 파생되는 읽기 전용 값. 운영자가 직접 못 바꾸게 disabled.
                기관을 바꾸면 위 selectOrg가 region_id를 동기화해 여기 표시가 갱신된다. */}
            <input className="input" disabled
                   value={regions.find(r=>r.id===form.region_id)?.name || '(미배정)'}
                   style={{background:'var(--bg-sub)', color:'var(--ink-2)', cursor:'not-allowed'}}/>
          </Field>
          <Field label="전화번호"><PhoneInput value={form.phone||''} onChange={e=>set('phone')(e.target.value)}/></Field>
          {/* 자격증 사진 첨부는 매니저 본인 가입 경로에서만 한다(운영자는 업로드하지 않음).
              편집 시 본인이 가입 때 올린 사진이 있으면 검증용으로 '보기' 링크만 제공한다. */}
          {editing!=='new' && form.has_license_image && (
            <Field label="자격증 사진">
              <a href={form.license_image_url} target="_blank" rel="noreferrer"
                 style={{fontSize:12, color:'var(--orange-500)'}}>자격증 사진 보기 ↗</a>
            </Field>
          )}
          {/* 운영자 = '검증자': 자격증·발급번호(매니저 본인 가입 시 등록)를 실제 확인했음을 표시.
              번호 입력칸은 없앴지만 '자격 검증' 확인 UI는 등록·편집 모달에 그대로 유지한다. */}
          <div style={{gridColumn:'1/-1', background:'var(--bg-sub)', borderRadius:8, padding:'10px 12px'}}>
            <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12.5, fontWeight:700}}>
              <input type="checkbox" checked={!!form.license_verified} onChange={e=>set('license_verified')(e.target.checked)}/>
              자격 검증 완료 — 실제 자격증·발급번호를 확인했습니다
            </label>
            <input className="input" style={{marginTop:8}} value={form.license_verification_note||''}
                   onChange={e=>set('license_verification_note')(e.target.value)}
                   placeholder="검증 근거 (예: 자격증 원본 대조 / 사본 수령)"/>
            {editing!=='new' && form.license_verified && form.license_verified_at && (
              <div className="muted" style={{fontSize:10, marginTop:6}}>
                검증: {form.license_verified_by_name||'승인자'} · {new Date(form.license_verified_at).toLocaleString('ko-KR')}
              </div>
            )}
          </div>
          <Field label="주간 최대시간 (계약)">
            <input className="input" type="number" min={0} max={80} step={1}
                   value={form.max_weekly_hours ?? 40}
                   onChange={e => set('max_weekly_hours')(e.target.value === '' ? 40 : Number(e.target.value))}/>
          </Field>
          {editing !== "new" && <Field label="상태">
            <select className="select" value={form.status} onChange={e=>set('status')(e.target.value)}>
              <option value="available">가용</option>
              <option value="busy">바쁨</option>
              <option value="overloaded">과부하</option>
              <option value="moving">이동중</option>
              <option value="offline">오프라인</option>
            </select>
          </Field>}
        </div>

        <div style={{marginTop:14}}>
          <div className="label" style={{marginBottom:6}}>개인 근무 위치 (소속 기관이 없을 때만 매칭 거리에 사용)</div>
          {(() => {
            const org = orgs.find(o => o.id === form.organization_id);
            const reg = regions.find(r => r.id === form.region_id);
            // 초기 지도 중심: 소속 기관 위치 우선(근무지), 없으면 권역 center.
            const center =
              org && org.latitude != null && org.longitude != null
                ? { lat: org.latitude, lng: org.longitude }
                : (reg && reg.center_latitude != null && reg.center_longitude != null
                    ? { lat: reg.center_latitude, lng: reg.center_longitude } : null);
            const value = form.latitude != null && form.longitude != null
              ? { lat: form.latitude, lng: form.longitude } : null;
            return (
              <LocationPicker
                value={value}
                center={center}
                onChange={(c) => setForm(f => ({ ...f, latitude: c.lat, longitude: c.lng }))}
              />
            );
          })()}
          <div className="muted" style={{fontSize:10, marginTop:4}}>
            소속 기관이 있으면 매칭 거리는 기관 위치를 기준으로 합니다. 이 핀은 기관 미등록 매니저에만 적용됩니다.
          </div>
        </div>

        {editing !== "new" && (
          <div style={{marginTop:18, padding:'14px 16px', background:'var(--bg-sub)', borderRadius:10}}>
            <div style={{fontSize:11, fontWeight:700, color:'var(--ink-2)', marginBottom:8, letterSpacing:'0.04em'}}>
              운영 통계 (자동 집계 · 편집 불가)
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, fontSize:12}}>
              <div><div className="muted" style={{fontSize:10, fontWeight:600}}>담당 케이스</div><div className="mono" style={{fontSize:13, fontWeight:700}}>{form.assigned_cases||0}</div></div>
              <div><div className="muted" style={{fontSize:10, fontWeight:600}}>부하율</div><div className="mono" style={{fontSize:13, fontWeight:700}}>{Math.round((form.load_ratio||0)*100)}%</div></div>
              <div><div className="muted" style={{fontSize:10, fontWeight:600}}>주간 시간</div><div className="mono" style={{fontSize:13, fontWeight:700}}>{form.weekly_hours||0} / {form.max_weekly_hours ?? 40}h</div></div>
              <div><div className="muted" style={{fontSize:10, fontWeight:600}}>평가</div><div className="mono" style={{fontSize:13, fontWeight:700}}>{(form.rating||0).toFixed(1)}</div></div>
            </div>
          </div>
        )}
      </Modal>

      {deletePrompt && (
        <DeletionConfirmModal
          resourceLabel="매니저"
          subject={{ name: deletePrompt.manager.name }}
          loading={deletePrompt.loading}
          impact={deletePrompt.impact ? {
            cases:   ["담당 케이스", deletePrompt.impact.cases   || 0],
            visits:  ["방문 기록",   deletePrompt.impact.visits  || 0],
            matches: ["매칭 이력",   deletePrompt.impact.matches || 0],
          } : null}
          deletable={deletePrompt.deletable}
          detachedNote="담당 케이스의 매니저 배정이 해제됩니다. 분리된 케이스는 재배정이 필요합니다."
          blockedNote={"방문·매칭·메시지 등 활동 이력이 남아 있어 삭제할 수 없습니다.\n퇴사한 매니저라면 행의 '퇴직' 버튼으로 처리하세요. 소속·이력은 보존되고 명부·매칭에서만 제외됩니다."}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
        />
      )}

      {assignTarget && (
        <ManagerAssignCaseModal
          manager={assignTarget}
          onClose={()=>setAssignTarget(null)}
          onAssigned={()=>{ setAssignTarget(null); load(); notifySaved("배정되었습니다"); }}
        />
      )}

      <Modal
        open={transfer != null}
        title={transfer ? `${transfer.manager.name} 소속 변경` : ""}
        onClose={()=>setTransfer(null)}
        width={460}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={()=>setTransfer(null)}>취소</button>
          <button className="btn btn-primary btn-md" onClick={submitTransfer} disabled={transfer?.saving}>
            {transfer?.saving ? "변경 중…" : "소속 변경"}
          </button>
        </>}
      >
        {transfer && (<>
          {transfer.err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{transfer.err}</div>}
          <div className="muted" style={{fontSize:12, marginBottom:12}}>
            현재 소속: <b>{transfer.manager.organization_name || '미소속'}</b>
            {transfer.manager.region_name ? ` · ${transfer.manager.region_name}` : ''}
          </div>
          <Field label="이동할 소속 기관 (필수)">
            {/* 케어매니저는 요양/재가(care) 기관에만 소속 가능. 권역 관리자에게는
                orgs 목록 자체가 서버(scoped_orgs)에서 자기 권역으로 한정돼 내려온다. */}
            <OrgCombobox
              orgs={orgs}
              value={transfer.orgId ?? null}
              onChange={id => setTransfer(t => ({ ...t, orgId: id }))}
              placeholder="이동할 기관 이름 검색 (필수)"
              width="100%"
            />
          </Field>
          <Field label="변경 사유 (필수)">
            <textarea className="input" rows={3} value={transfer.reason}
                      onChange={e=>setTransfer(t=>({ ...t, reason: e.target.value }))}
                      placeholder="예: 기관 통폐합 / 매니저 요청 확인 / 권역 내 인력 재배치"/>
          </Field>
          <div className="muted" style={{fontSize:10.5, marginTop:4}}>
            소속 변경은 감사 로그에 사유와 함께 기록되며, 매니저 본인에게 알림이 전송됩니다. 자기 권역 안의 기관으로만 이동할 수 있습니다.
          </div>
        </>)}
      </Modal>

      <Modal
        open={reinstateModal != null}
        title={reinstateModal ? `${reinstateModal.manager.name} 복직 처리` : ""}
        onClose={()=>setReinstateModal(null)}
        width={460}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={()=>setReinstateModal(null)}>취소</button>
          <button className="btn btn-primary btn-md" onClick={submitReinstate} disabled={reinstateModal?.saving}>
            {reinstateModal?.saving ? "처리 중…" : "복직 처리"}
          </button>
        </>}
      >
        {reinstateModal && (<>
          {reinstateModal.err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{reinstateModal.err}</div>}
          <div className="muted" style={{fontSize:11.5, marginBottom:12, padding:'8px 10px', background:'var(--amber-50)', color:'#8a6326', borderRadius:8, lineHeight:1.5}}>
            이 매니저는 퇴직 중 소속 기관이 삭제되어 <b>미소속</b> 상태입니다. 복직하려면 재직할 소속 기관(요양/재가)을 지정해야 합니다.
          </div>
          <Field label="복직 소속 기관 (필수)">
            <OrgCombobox
              orgs={orgs}
              value={reinstateModal.orgId ?? null}
              onChange={id => setReinstateModal(t => ({ ...t, orgId: id }))}
              placeholder="소속 기관 이름 검색 (필수)"
              width="100%"
            />
          </Field>
          <div className="muted" style={{fontSize:10.5, marginTop:4}}>
            복직 시 매니저는 다시 명부·매칭·부하 집계에 포함되며, 감사 로그에 기록됩니다.
          </div>
        </>)}
      </Modal>

      <Modal
        open={retire != null}
        title={retire ? `${retire.manager.name} 퇴직 처리` : ""}
        onClose={()=>setRetire(null)}
        width={460}
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={()=>setRetire(null)}>취소</button>
          <button className="btn btn-md" onClick={submitRetire} disabled={retire?.saving}
                  style={{background:'var(--rose-500)', color:'#fff'}}>
            {retire?.saving ? "처리 중…" : "퇴직 처리"}
          </button>
        </>}
      >
        {retire && (<>
          {retire.err && <div style={{background:'var(--rose-50)', color:'var(--rose-500)', padding:'8px 12px', borderRadius:8, marginBottom:12, fontSize:12}}>{retire.err}</div>}
          <div className="muted" style={{fontSize:12, marginBottom:12}}>
            소속: <b>{retire.manager.organization_name || '미소속'}</b>
            {retire.manager.region_name ? ` · ${retire.manager.region_name}` : ''}
          </div>
          <div className="muted" style={{fontSize:11.5, marginBottom:12, padding:'8px 10px', background:'var(--amber-50)', color:'#8a6326', borderRadius:8, lineHeight:1.5}}>
            소속 기관과 방문·매칭 등 이력은 그대로 보존됩니다. 이 매니저는 활성 명부·신규 매칭·부하 집계에서 제외됩니다. 복직은 언제든 가능합니다.
          </div>
          <Field label="퇴직 사유 (필수)">
            <textarea className="input" rows={3} value={retire.reason}
                      onChange={e=>setRetire(t=>({ ...t, reason: e.target.value }))}
                      placeholder="예: 퇴사 / 계약 종료 / 장기 휴직"/>
          </Field>
          <div className="muted" style={{fontSize:10.5, marginTop:4}}>
            퇴직 처리는 감사 로그에 사유와 함께 기록됩니다.
          </div>
        </>)}
      </Modal>
      {saveToast}
    </div>
  );
}
export { Management };
